/**
 * 报价方案费用合计（按币种）计算工具
 *
 * 与后端 Quotation*FacadeV2#fillChargingItemTotals 同口径：
 * 1. 非按箱计费（billingUnitType=2）：按计费单位换算——单位为「计费重/重量/体积」时取 单价×询价对应数量，
 *    其余（票等）取单价原值，最后按最低/最高收费钳制；
 * 2. 按箱计费（billingUnitType=1 或为空）：仅整箱/集装箱服务按 Σ 箱型单价 × 箱量累加，箱量缺失的箱型按 1 箱计；
 * 3. 询价未维护箱型箱量时，以「基础费用」箱型价格中出现过的箱型为准（价格为 0 也算有值），每个箱型按 1 箱计。
 */

/** 到付（与各页面 constants 中 PAYMENT_TERM.COLLECT 一致） */
const PAYMENT_COLLECT = 2

/** 参与计算的费用行（各运输方式费用条目的公共字段） */
export interface ChargingItemLike {
  /** 费用类型，用于排除潜在费用、定位基础费用行 */
  feeType?: unknown
  /** 币种 */
  currency?: unknown
  /** 支付方式：1=预付 2=到付 */
  paymentType?: unknown
  /** 计费单位类型：1=按箱 2=非按箱 */
  billingUnitType?: unknown
  /** 计费单位ID */
  measureId?: unknown
  /** 单价（原价槽 / 卖价槽） */
  unitRate?: unknown
  unitRateSell?: unknown
  /** 箱型价格 JSON（原价槽 / 卖价槽） */
  eqPrice?: unknown
  eqPriceSell?: unknown
  /** 最低/最高收费（原价槽 / 卖价槽） */
  minCharge?: unknown
  minChargeSell?: unknown
  maxCharge?: unknown
  maxChargeSell?: unknown
}

/** 箱型箱量来源（询价货物行：集装箱/整箱） */
export interface ContainerQuantitySource {
  containerType?: unknown
  containerNum?: unknown
}

/** 单条费用行与方案合计的计算上下文 */
export interface PlanChargingContext {
  /** true=取卖价槽（询价人模式），false=取原价槽 */
  useSell: boolean
  /** 是否按箱型箱量累加（整箱/集装箱服务），false 时按箱费用不参与合计 */
  containerBased: boolean
  /** 忽略 billingUnitType，强制按计费单位换算（空运费用口径） */
  chargeByUnit?: boolean
  /** 箱型编码(大写) → 箱量 */
  containerQuantities: Record<string, number>
  /** measureId → 计费单位中文名 */
  unitNameMap: Record<string, string>
  /** 计费重（单位名称为「计费重」时使用） */
  chargeableWeight?: number | null
  /** 重量（单位名称为「重量」时使用） */
  weight?: number | null
  /** 体积（单位名称为「体积」时使用） */
  cubage?: number | null
}

export interface CurrencyAmount {
  currency: string
  amount: string
}

export interface PlanCurrencyTotals {
  /** 预付合计 */
  prepaid: CurrencyAmount[]
  /** 到付合计 */
  collect: CurrencyAmount[]
  /** 不区分预付/到付的合计 */
  total: CurrencyAmount[]
}

/** 转数字：空值或非数字返回 null（与后端「有值才计算」口径一致） */
const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

/** 箱型编码归一：去空格转大写 */
const normalizeContainerType = (value: unknown) =>
  String(value ?? '').trim().toUpperCase()

/** 解析箱型价格（兼容 JSON 字符串与对象） */
export const parseEqPriceMap = (eqPrice: unknown): Record<string, number> => {
  if (!eqPrice) return {}
  if (typeof eqPrice === 'string') {
    try {
      const parsed = JSON.parse(eqPrice)
      return parsed && typeof parsed === 'object' ? parsed as Record<string, number> : {}
    } catch {
      return {}
    }
  }
  return typeof eqPrice === 'object' ? eqPrice as Record<string, number> : {}
}

/**
 * 计算实际参与计算的箱型箱量：
 * 询价已维护箱型箱量时直接使用；未维护时以「基础费用」箱型价格中出现过的箱型为准（价格为 0 也算有值），每个箱型按 1 箱计。
 *
 * @param containers 询价箱型箱量（集装箱/整箱）
 * @param baseFeeItems 方案费用行（用于兜底箱型）
 * @param baseFeeType 基础费用 feeType
 */
export const buildContainerQuantities = (
  containers?: ReadonlyArray<ContainerQuantitySource> | null,
  baseFeeItems?: ReadonlyArray<ChargingItemLike> | null,
  baseFeeType?: number
): Record<string, number> => {
  const quantities: Record<string, number> = {}
  ;(containers || []).forEach(container => {
    const code = normalizeContainerType(container?.containerType)
    const num = toNumber(container?.containerNum)
    if (!code || num === null || num <= 0) return
    quantities[code] = (quantities[code] || 0) + num
  })
  if (Object.keys(quantities).length) return quantities
  if (baseFeeType === undefined) return quantities

  ;(baseFeeItems || []).forEach(item => {
    if (toNumber(item?.feeType) !== baseFeeType) return
    // 原价槽/卖价槽任一出现该箱型即视为有值
    ;[item?.eqPrice, item?.eqPriceSell].forEach(rawEqPrice => {
      Object.keys(parseEqPriceMap(rawEqPrice)).forEach(code => {
        const key = normalizeContainerType(code)
        if (key) quantities[key] = 1
      })
    })
  })
  return quantities
}

/** 按计费单位名称取询价数量：计费重/重量/体积；其余单位（票等）返回 null */
const resolveUnitQuantity = (unitName: string, ctx: PlanChargingContext): number | null => {
  if (!unitName) return null
  if (unitName.startsWith('计费重')) return toNumber(ctx.chargeableWeight)
  if (unitName.startsWith('重量')) return toNumber(ctx.weight)
  if (unitName.startsWith('体积')) return toNumber(ctx.cubage)
  return null
}

/** 按最低/最高收费钳制金额（低于下限取下限，高于上限取上限） */
const clampCharge = (amount: number, minCharge: number | null, maxCharge: number | null) => {
  if (minCharge !== null && amount < minCharge) return minCharge
  if (maxCharge !== null && amount > maxCharge) return maxCharge
  return amount
}

/** 单条费用行金额（与后端 resolveItemTotal 同口径） */
export const resolveChargingItemAmount = (
  item: ChargingItemLike,
  ctx: PlanChargingContext
): number => {
  if (!item) return 0
  // 非按箱计费（或强制按计费单位）：按计费单位换算单价
  if (ctx.chargeByUnit || toNumber(item.billingUnitType) === 2) {
    const unitRate = toNumber(ctx.useSell ? item.unitRateSell : item.unitRate)
    if (unitRate === null) return 0
    const unitName = String(ctx.unitNameMap[String(item.measureId ?? '')] || '').trim()
    const quantity = resolveUnitQuantity(unitName, ctx)
    // 「票」及未识别单位取单价原值；数量为空或非正数时同样退化为单价
    const amount = quantity !== null && quantity > 0 ? unitRate * quantity : unitRate
    return clampCharge(
      amount,
      toNumber(ctx.useSell ? item.minChargeSell : item.minCharge),
      toNumber(ctx.useSell ? item.maxChargeSell : item.maxCharge)
    )
  }
  // 按箱计费（billingUnitType=1 或为空）：仅整箱/集装箱服务按箱型箱量累加
  if (!ctx.containerBased) return 0
  const eqPrice = parseEqPriceMap(ctx.useSell ? item.eqPriceSell : item.eqPrice)
  return Object.entries(eqPrice).reduce((total, [containerType, price]) => {
    const unitPrice = toNumber(price)
    if (unitPrice === null) return total
    const quantity = ctx.containerQuantities[normalizeContainerType(containerType)] ?? 1
    return total + unitPrice * quantity
  }, 0)
}

/** Map<币种, 金额> 转展示用数组 */
const toAmountList = (amountMap: Map<string, number>): CurrencyAmount[] =>
  Array.from(amountMap.entries()).map(([currency, amount]) => ({
    currency,
    amount: amount.toFixed(2)
  }))

/**
 * 计算方案合计（按币种），自动排除潜在费用并区分预付/到付
 *
 * @param items 方案费用行
 * @param ctx 计算上下文
 * @param excludeFeeTypes 不计入合计的费用类型（潜在费用）
 * @param resolveAmount 单行金额的特殊实现（如空运基础运费按命中密度等级与重量段档位价结算），默认按计费单位换算
 */
export const calcPlanCurrencyTotals = (
  items: ReadonlyArray<ChargingItemLike> | null | undefined,
  ctx: PlanChargingContext,
  excludeFeeTypes: ReadonlyArray<number> = [],
  resolveAmount?: (item: ChargingItemLike) => number
): PlanCurrencyTotals => {
  const prepaid = new Map<string, number>()
  const collect = new Map<string, number>()

  ;(items || []).forEach(item => {
    const feeType = toNumber(item?.feeType)
    if (feeType !== null && excludeFeeTypes.includes(feeType)) return
    const currency = String(item?.currency ?? '').trim()
    if (!currency) return
    const amount = resolveAmount ? resolveAmount(item) : resolveChargingItemAmount(item, ctx)
    if (!(amount > 0)) return
    const map = toNumber(item.paymentType) === PAYMENT_COLLECT ? collect : prepaid
    map.set(currency, (map.get(currency) || 0) + amount)
  })

  const total = new Map<string, number>()
  ;[prepaid, collect].forEach(map => {
    map.forEach((amount, currency) => {
      total.set(currency, (total.get(currency) || 0) + amount)
    })
  })

  return {
    prepaid: toAmountList(prepaid),
    collect: toAmountList(collect),
    total: toAmountList(total)
  }
}

/** 方案列表项合计文案（不区分预付/到付），形如 "USD 123.00 / CNY 456.00" */
export const formatCurrencyTotalsInline = (totals: CurrencyAmount[]): string =>
  totals.map(({ currency, amount }) => `${currency} ${amount}`).join(' / ')

/** 构建计算上下文（各页面按自身服务类型/费用类型传参） */
export const buildPlanChargingContext = (options: {
  /** true=询价人模式，取卖价槽 */
  useSell: boolean
  /** 是否整箱/集装箱服务（按箱型箱量累加） */
  containerBased: boolean
  /** 忽略 billingUnitType，强制按计费单位换算（空运费用口径） */
  chargeByUnit?: boolean
  /** measureId → 计费单位中文名 */
  unitNameMap?: Record<string, string> | null
  /** 询价单（提供计费重/重量/体积；取不到时对应费用按「票」处理） */
  inquiry?: Record<string, unknown> | null
  /** 询价箱型箱量 */
  containers?: ReadonlyArray<ContainerQuantitySource> | null
  /** 方案费用行（兜底箱型来源） */
  chargingItems?: ReadonlyArray<ChargingItemLike> | null
  /** 基础费用 feeType */
  baseFeeType?: number
}): PlanChargingContext => ({
  useSell: options.useSell,
  containerBased: options.containerBased,
  chargeByUnit: options.chargeByUnit,
  unitNameMap: options.unitNameMap || {},
  chargeableWeight: toNumber(options.inquiry?.chargeableWeight),
  weight: toNumber(options.inquiry?.weight),
  cubage: toNumber(options.inquiry?.cubage),
  containerQuantities: buildContainerQuantities(options.containers, options.chargingItems, options.baseFeeType)
})
